// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect } from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import ChatInterfaceFull from './components/ChatInterfaceFull'
import Login from './components/Login'
import { lightTheme, buildTheme } from './theme'
import { useAppearance, applyDocumentThemeClasses } from './lib/appearance'
import './App.css'

// Read initial state synchronously to prevent flash
const getInitialState = () => {
  const token = localStorage.getItem('cme_token')
  const userData = localStorage.getItem('cme_user')
  let hasToken = false

  if (token && userData) {
    try {
      const user = JSON.parse(userData)

      // Redirect super_admin to their dashboard
      if (user.role === 'super_admin') {
        window.location.href = 'super-admin.html'
        return { hasToken: false }
      }

      hasToken = true
    } catch (e) {
      // ignore parse errors
    }
  }

  // Apply dark/terminal classes and colors immediately to prevent flash
  applyDocumentThemeClasses()

  return { hasToken }
}

const initialState = getInitialState()

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null) // null = checking, true/false = known
  const { mode, style, scheme } = useAppearance()

  // Verify token on mount and refresh user data
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('cme_token')
      if (!token) {
        setIsAuthenticated(false)
        return
      }

      try {
        // Use /users/me to both verify token AND get fresh user data
        const response = await fetch('/users/me', {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response.ok) {
          const userData = await response.json()
          localStorage.setItem('cme_user', JSON.stringify(userData))
          setIsAuthenticated(true)
        } else {
          // Token invalid - clear storage and show login
          localStorage.removeItem('cme_token')
          localStorage.removeItem('cme_user')
          setIsAuthenticated(false)
        }
      } catch (e) {
        // Network error - assume token is valid and let ChatInterfaceFull handle it
        setIsAuthenticated(initialState.hasToken)
      }
    }

    verifyToken()
  }, [])

  const handleLoginSuccess = () => {
    setIsAuthenticated(true)
  }

  const handleLogout = () => {
    localStorage.removeItem('cme_token')
    localStorage.removeItem('cme_user')
    localStorage.removeItem('cme_company')
    localStorage.removeItem('cme_original_token')
    localStorage.removeItem('cme_impersonating')
    setIsAuthenticated(false)
  }

  // Show nothing while checking auth
  if (isAuthenticated === null) {
    return null
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Login onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={buildTheme(mode, style, scheme)}>
      <CssBaseline />
      <ChatInterfaceFull onLogout={handleLogout} />
    </ThemeProvider>
  )
}

export default App
